import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { History, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';

/**
 * CsvBatchesPanel
 * ---------------
 * Lists every direct-commit CSV import batch with row count, date range,
 * investor count, and an explicit DELETE control. Deleting a batch wipes
 * those rows from `investor_transactions` AND cascade-cleans every
 * `transaction_allocations` detail entry that referenced them — so you can
 * safely re-upload a corrected CSV without phantom allocations remaining.
 */
export function CsvBatchesPanel({ API_URL, headers }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/committed-batches`, { headers });
      setBatches(r.data.batches || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [API_URL, headers]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const handleDelete = async (batch) => {
    const msg = `Delete the entire CSV upload "${batch.batch_name || batch.batch_id}"?\n\n` +
                `• ${batch.row_count} investor transactions\n` +
                `• ${batch.investor_count} investor(s)\n` +
                `• Date range: ${batch.min_date?.slice(0,10)} → ${batch.max_date?.slice(0,10)}\n\n` +
                `Any allocations created from these rows will be reversed and ` +
                `the bank txns they touched will return to the unmatched list. ` +
                `This action CANNOT be undone — but you can re-upload a corrected CSV afterward.`;
    if (!window.confirm(msg)) return;
    setDeletingId(batch.batch_id);
    try {
      const r = await axios.delete(
        `${API_URL}/api/admin/investor-transactions/committed-batches/${encodeURIComponent(batch.batch_id)}`,
        { headers }
      );
      toast.success(r.data.message || `Deleted ${r.data.deleted_count || 0} rows`);
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card data-testid="csv-batches-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4" /> CSV Import History
            </CardTitle>
            <CardDescription>
              Every direct-commit CSV upload. Delete a batch to surgically undo a bad upload —
              allocations that referenced those rows are cleaned automatically.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchBatches} disabled={loading} data-testid="csv-batches-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {batches.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8">No CSV uploads yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch</TableHead>
                <TableHead className="text-right">Rows</TableHead>
                <TableHead className="text-right">Investors</TableHead>
                <TableHead>Date range</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((b, i) => (
                <TableRow key={b.batch_id} data-testid={`csv-batch-row-${i}`}>
                  <TableCell>
                    <div className="text-sm font-medium truncate max-w-[260px]">
                      {b.batch_name || <span className="text-slate-400 italic">(no filename)</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono truncate max-w-[260px]">
                      {b.batch_id}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">{b.row_count}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm">{b.investor_count}</TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {b.min_date?.slice(0,10)} → {b.max_date?.slice(0,10)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {b.uploaded_at ? new Date(b.uploaded_at).toLocaleString('en-ZA') : '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(b)}
                      disabled={deletingId === b.batch_id}
                      data-testid={`csv-batch-delete-${i}`}
                    >
                      {deletingId === b.batch_id ? (
                        <RefreshCw className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="mt-3 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900">
          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
          <span>
            Tip: to fix a bad upload, <strong>delete the batch here</strong>, then re-upload the corrected CSV
            via "Upload Transaction CSV" above. Or use <strong>Re-import &amp; Replay</strong> on the recon tab
            to overwrite all CSV rows in one shot while re-binding allocations.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default CsvBatchesPanel;
